import { Link } from "react-router-dom";
import { Matchup, MatchupEntry } from "../models";
import { formatKickoff, matchupStatus, nextKickoff, sideProgress } from "../scoring";
import { ChipBadge } from "./ChipBadge";
import { WinProbability } from "./WinProbability";
import "./MatchupCard.css";

interface MatchupCardProps {
	matchup: Matchup;
	/** Inverts the card to call out the viewer's own matchup. */
	featured?: boolean;
}

const resultClass = (side: MatchupEntry) => (side.result === "win" || side.result === "loss" ? ` ${side.result}` : "");

export const MatchupCard = ({ matchup: m, featured = false }: MatchupCardProps) => {
	const status = matchupStatus(m);
	const home = sideProgress(m.home.players);
	const away = sideProgress(m.away.players);

	const statusLabel = status === "upcoming" ? `Kicks off ${formatKickoff(nextKickoff(m))}` : status === "done" ? "Full time" : "In play";
	const progressLabel = `${home.played + home.inProgress}/${home.total} · ${away.played + away.inProgress}/${away.total}`;
	const showProjection = m.prediction != null;

	return (
		<Link to={`/matchups/${m.id}`} className={`matchup-card${featured ? " featured" : ""}`} data-testid={`matchup-${m.id}`}>
			<div className="matchup-status">
				<span className="matchup-status-label">{statusLabel}</span>
			</div>

			<div className="matchup-scoreline">
				<div className="matchup-side">
					<div className="matchup-team">{m.home.team}</div>
					<div className="matchup-manager">{m.home.name}</div>
					<ChipBadge chip={m.home.chip} hit={m.home.basePoints} inverted={featured} />
				</div>
				<div className="matchup-score mono">
					<span className={`matchup-points${resultClass(m.home)}`}>{m.home.totalPoints}</span>
					<span className="matchup-score-sep">—</span>
					<span className={`matchup-points${resultClass(m.away)}`}>{m.away.totalPoints}</span>
				</div>
				<div className="matchup-side right">
					<div className="matchup-team">{m.away.team}</div>
					<div className="matchup-manager">{m.away.name}</div>
					<ChipBadge chip={m.away.chip} hit={m.away.basePoints} inverted={featured} right />
				</div>
			</div>

			{m.prediction && (
				<div className="matchup-prediction">
					<WinProbability prediction={m.prediction} inverted={featured} />
				</div>
			)}

			<div className="matchup-meta">
				<span className="matchup-projected">{showProjection && m.prediction && <>PROJ&nbsp;&nbsp;<strong>{m.prediction.projectedHome}</strong></>}</span>
				<span className="matchup-progress" title="Players played or in play">
					{progressLabel}
				</span>
				<span className="matchup-projected right">{showProjection && m.prediction && <><strong>{m.prediction.projectedAway}</strong>&nbsp;&nbsp;PROJ</>}</span>
			</div>
		</Link>
	);
};
